import { StepperOrientation, STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper'
import { ChangeDetectorRef, Component, ElementRef, Inject, Optional, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { MatDatepicker } from '@angular/material/datepicker'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatStepper } from '@angular/material/stepper'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { CallerService } from '../../services/caller.service'
import { HttpErrorResponse } from '@angular/common/http'
import { AdminService } from '../../services/admin.service'
import { AnalyticsService } from '../../services/analytics.service'
import { HAS_POSTED_BEFORE_KEY, PushNotificationService } from '../../services/push-notification.service'
import { ACCOMMODATION_TYPES } from '../../utils/constants.util'
import { amountValidator, focusFirstInvalid, phoneNumberValidator } from '../../utils/form-validation.util'

interface ImagePreview {
    file: File
    dataUrl: string
}

interface Rooms {
    value: string
    viewValue: string
}

@Component({
    selector: 'app-create-new-accommodation', // only opened as a dialog, never used as an element
    templateUrl: './create-new-accommodation.component.html',
    styleUrls: ['./create-new-accommodation.component.css'],
    providers: [
        {
            provide: STEPPER_GLOBAL_OPTIONS,
            // showError: a step left with an error shows it in its header.
            useValue: { displayDefaultIndicatorType: false, showError: true },
        },
    ],
})
export class CreateNewAccommodationComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>()
    @ViewChild('stepper') stepper!: MatStepper
    readonly accommodationTypes = ACCOMMODATION_TYPES
    @ViewChild('imgFileInput', { read: ElementRef<HTMLInputElement> })
        imgFileInput!: ElementRef<HTMLInputElement>
    
    constructor(
        private _formBuilder: FormBuilder,
        private createPostDialogRef: MatDialogRef<CreateNewAccommodationComponent>,
        private callerService: CallerService,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: { isAdmin?: boolean } | null,
        private adminService: AdminService,
        private analyticsService: AnalyticsService,
        private pushNotificationService: PushNotificationService,
        private changeDetector: ChangeDetectorRef,
    ) {}

    accommodationImagesPreview: Array<ImagePreview> = []

    availableHouseRooms: Array<Rooms> = [
        {
            value: 'Bathroom',
            viewValue: 'Bathroom',
        },
        {
            value: 'Sitting Room',
            viewValue: 'Sitting Room',
        },
        {
            value: 'Toilet',
            viewValue: 'Toilet',
        },
        {
            value: 'Kitchen',
            viewValue: 'Kitchen',
        },
        {
            value: 'Bathroom & Toilet',
            viewValue: 'Bathroom & Toilet',
        },
        {
            value: 'Hallway or Corridor',
            viewValue: 'Hallway/Corridor',
        },
        {
            value: 'Frontage',
            viewValue: 'Frontage',
        },
    ]

    /** Whether it's been posted - only then can the Done step be reached. */
    posted = false
    postingAccommodation: boolean = false;
    draftingAccommodation: boolean = false;

    /** One group a step, so a step can't be left with an error in it (the stepper is linear). */
    accommodationFormGroup = new FormGroup({
        specs: new FormGroup({
            accommodation_type: new FormControl('', [Validators.required]),
            available_rooms: this._formBuilder.group({
                '1': false, // Sitting-room
                '2': false, // Kitchen
                '3': false, // Bedroom
                '4': false, // Toilet
                '5': false, // Bathroom
                '6': false, // Dining-room
            }),
        }),
        location: new FormGroup({
            address: new FormControl(''),
            directions: new FormControl(''),
        }),
        status: new FormGroup({
            status: new FormControl('', [Validators.required]),
            rent_expire_date: new FormControl<Date | string | null>(''),
            ideal_roommate: new FormControl(''),
            occupant_description: new FormControl(''),
        }),
        contact: new FormGroup({
            phone_numbers: this._formBuilder.array<FormControl<string | null>>([]),
        }),
        // The backend needs the interval, and the rent unless it's free.
        cost: new FormGroup({
            rent_interval: new FormControl('', [Validators.required]),
            rent: new FormControl('', [Validators.required, amountValidator(1)]),
        }),
    })

    get specsGroup() { return this.accommodationFormGroup.controls.specs }
    get locationGroup() { return this.accommodationFormGroup.controls.location }
    get statusGroup() { return this.accommodationFormGroup.controls.status }
    get contactGroup() { return this.accommodationFormGroup.controls.contact }
    get costGroup() { return this.accommodationFormGroup.controls.cost }

    /** A sublet's rent can't have run out already. */
    readonly today = new Date()

    /** The calendar gives focus back to its input as it closes - which mustn't open it again. */
    datepickerJustClosed = false

    /** Opens the calendar when its input gets focus, so the date is picked rather than typed. */
    openDatepicker(picker: MatDatepicker<Date>): void {
        if (this.datepickerJustClosed) {
            this.datepickerJustClosed = false
            return
        }
        picker.open()
    }

    stepperOrientation: StepperOrientation = 'vertical'

    private readonly intervalDisplayMap: Record<string, string> = {
        yearly: 'year',
        quarterly: 'quarter',
        monthly: 'month',
    };

    /** Whether to show the error at `path` ('cost.rent'; any error without `error`): once it's touched. */
    showError(path: string, error?: string): boolean {
        const c = this.accommodationFormGroup.get(path)
        return !!c && c.touched && (error ? c.hasError(error) : c.invalid)
    }

    get phoneNumbersFormArray(): FormArray {
        return this.contactGroup.controls.phone_numbers as FormArray
    }

    get rentInputLabel(): string {
        const rentInterval = this.costGroup.controls.rent_interval.value
        const status = this.statusGroup.controls.status.value

        let label = 'Rent';

        if (rentInterval && this.intervalDisplayMap[rentInterval]) {
            label += ` per ${this.intervalDisplayMap[rentInterval]}`;
        }

        if (status === 'seeking_roommate') {
            label += ' for your roommate';
        }

        return label;
    }

    getPhoneNumberControl(index: number): FormControl {
        return this.phoneNumbersFormArray.at(index) as FormControl;
    }

    addPhoneNumber(): void {
        // An empty one is fine - it's left out - but one they've typed in has to be a phone number.
        const phoneNumberControl = new FormControl('', [phoneNumberValidator]);
        this.phoneNumbersFormArray.push(phoneNumberControl);
    }

    removePhoneNumber(index: number): void {
        this.phoneNumbersFormArray.removeAt(index);
    }

    ngOnInit(): void {
        this.callerService.isSmallScreen$.pipe(takeUntil(this.destroy$)).subscribe((small) => {
            this.stepperOrientation = small ? 'vertical' : 'horizontal'
        })

        // Subletting: when the rent runs out is needed (an estimate will do); otherwise it isn't asked.
        const rentExpireDate = this.statusGroup.controls.rent_expire_date
        this.statusGroup.controls.status.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((status) => {
            if (status === 'active_rent') {
                rentExpireDate.addValidators(Validators.required)
            } else {
                rentExpireDate.removeValidators(Validators.required)
                rentExpireDate.setValue('')
                rentExpireDate.markAsUntouched()
            }
            rentExpireDate.updateValueAndValidity()
        })

        // A free place has no rent to ask for.
        const rent = this.costGroup.controls.rent
        this.costGroup.controls.rent_interval.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((interval) => {
            if (interval === 'free') {
                rent.clearValidators()
                rent.setValue('')
            } else {
                rent.setValidators([Validators.required, amountValidator(1)])
            }
            rent.updateValueAndValidity()
        })
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    /**
     * The step's Next: on to the next step, or - with an error in this one - show every error in it and take
     * them to the first, instead of the stepper silently not moving.
     */
    next(group: AbstractControl, stepContent: HTMLElement): void {
        if (group.invalid) {
            this.showErrorsIn(group, stepContent)
            return
        }
        this.stepper.next()
    }

    private showErrorsIn(group: AbstractControl, stepContent: HTMLElement): void {
        group.markAllAsTouched()
        // After change detection has drawn the errors.
        setTimeout(() => focusFirstInvalid(stepContent))
    }

    close(): void {
        this.createPostDialogRef.close()
    }

    onFileSelected(event: Event): void {
        const files = (event.target as HTMLInputElement).files
        Array.from(files ?? []).forEach((file) => this.processImage(file))
    }

    processImage(file: File) {
        const reader = new FileReader()
        reader.onerror = () => this.callerService.showNotification(`Couldn't read ${file.name}. Try another file.`)
        reader.onload = () => this.accommodationImagesPreview.push({ file, dataUrl: String(reader.result) })
        reader.readAsDataURL(file)
    }

    /** The steps' fields as the backend takes them - one flat form - and the photos still picked. */
    buildFormData(): FormData {
        const formData = new FormData()
        const { specs, location, status, contact, cost } = this.accommodationFormGroup.getRawValue()
        const rooms = Object.entries(specs.available_rooms ?? {}).filter(([, picked]) => picked).map(([id]) => id)
        const phoneNumbers = (contact.phone_numbers ?? []).map((phone) => phone?.trim()).filter((phone) => !!phone)
        const fields: Record<string, unknown> = {
            accommodation_type: specs.accommodation_type,
            available_rooms: rooms.join(','),
            ...location,
            ...status,
            phone_numbers: phoneNumbers.join(','),
            ...cost,
        }
        for (const [field, value] of Object.entries(fields)) formData.set(field, String(value ?? ''))
        for (const { file } of this.accommodationImagesPreview) {
            formData.append(`accommodationMedia-${file.lastModified}-${file.size}-${file.name}`, file)
        }
        return formData
    }

    removePreviewImage(i: number) {
        // It's only uploaded if it's still here when they post (buildFormData).
        this.accommodationImagesPreview.splice(i, 1)

        // clear the input. (incase the same file is deleted and selected again)
        this.imgFileInput.nativeElement.value = ''
    }

    /** Post it, or save it as a draft - from the last step, with `costStep` its content. */
    submit(costStep: HTMLElement, isDraftPost = false): void {
        // The steps before were checked on the way here (the stepper is linear), so only this one is left.
        if (this.accommodationFormGroup.invalid) {
            this.showErrorsIn(this.costGroup, costStep)
            this.callerService.showNotification('Check the fields marked in red')
            return
        }

        const formData = this.buildFormData()
        if (isDraftPost) {
            formData.append('is_draft', '1')
            this.draftingAccommodation = true
        } else {
            this.postingAccommodation = true
        }
        const isAdmin = this.data?.isAdmin && this.adminService?.isAdminLoggedIn
        const req$ = isAdmin
            ? this.adminService.createAccommodationPost(formData)
            : this.callerService.createNewAccommodationPost(formData)
        req$.subscribe({
          next: (res: any) => {
            if (res.status === 200 || res.status === 201) {
                this.analyticsService.postCreated('accommodation')

                if (isDraftPost) {
                    this.callerService.showNotification('Draft saved.')
                    this.createPostDialogRef.close()
                } else if (isAdmin) {
                    this.callerService.showNotification('Accommodation posted (by admin)')
                    this.createPostDialogRef.close(true)
                } else {
                    this.posted = true
                    this.changeDetector.detectChanges() // so the stepper sees the last step completed
                    this.stepper.next()

                    if (!localStorage.getItem(HAS_POSTED_BEFORE_KEY)) {
                        localStorage.setItem(HAS_POSTED_BEFORE_KEY, 'true')
                        this.pushNotificationService.maybePromptToEnable()
                    }
                }
            }
            this.postingAccommodation = false
            this.draftingAccommodation = false
          },
          error: (err: HttpErrorResponse) => {
            this.showRequestError(err.error)
            this.postingAccommodation = false
            this.draftingAccommodation = false
          }
        })
    }

    /** The backend's message ({ message } - or that as a string), so a turned-down post never fails silently. */
    showRequestError(error: any) {
        let message: string | undefined = error?.message
        if (typeof error === 'string') {
            try {
                message = JSON.parse(error)?.message
            } catch {}
        }
        this.callerService.showNotification(message || 'Couldn\'t post it. Try again.')
    }
}
